/**
 * File Storage Page
 *
 * Super admin only — browse the public S3 bucket contents.
 */

import React, { useState, useEffect } from 'react'
import { storageAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge
} from '../components/ui'

const S3_PUBLIC_URL = 'https://5axis-intake-public.s3.amazonaws.com'

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg']

function isImageFile(key) {
  const ext = key.split('.').pop().toLowerCase()
  return IMAGE_EXTENSIONS.includes(ext)
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// The gate is its own component so the page's hooks always run in the same
// order: hooks after an early return crash React when the condition flips.
export default function FileStoragePage() {
  const { user } = useAuthStore()
  // Super admin gate
  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>This page is restricted to super administrators.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }
  return <FileStorageContent />
}

function FileStorageContent() {
  const [prefix, setPrefix] = useState('')
  const [folders, setFolders] = useState([])
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedFile, setExpandedFile] = useState(null) // Track which file preview is expanded


  const fetchContents = async (p) => {
    try {
      setLoading(true)
      setError(null)
      const response = await storageAPI.browse(p)
      if (response.data.success) {
        setFolders(response.data.data.folders || [])
        setFiles(response.data.data.files || [])
      }
    } catch (err) {
      console.error('[FILE STORAGE] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContents(prefix)
  }, [prefix])

  // Build breadcrumb segments from prefix
  const segments = prefix ? prefix.replace(/\/$/, '').split('/') : []

  const navigateTo = (newPrefix) => {
    setPrefix(newPrefix)
    setExpandedFile(null) // Close any open preview when navigating
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">File Storage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Browse public S3 bucket contents
        </p>
      </div>

      {/* Breadcrumb */}
      <Card>
        <CardContent className="py-3 px-4">
          <nav className="flex items-center gap-1 text-sm flex-wrap">
            <button
              onClick={() => navigateTo('')}
              className={`hover:underline font-medium ${prefix === '' ? 'text-foreground' : 'text-primary'}`}
            >
              /
            </button>
            {segments.map((seg, i) => {
              const segPath = segments.slice(0, i + 1).join('/') + '/'
              const isLast = i === segments.length - 1
              return (
                <span key={segPath} className="flex items-center gap-1">
                  <span className="text-muted-foreground">/</span>
                  <button
                    onClick={() => navigateTo(segPath)}
                    className={`hover:underline ${isLast ? 'text-foreground font-medium' : 'text-primary'}`}
                  >
                    {seg}
                  </button>
                </span>
              )
            })}
          </nav>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-destructive">
              <p>Error: {error}</p>
              <Button variant="outline" onClick={() => fetchContents(prefix)} className="mt-3">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground animate-pulse">Loading...</div>
          </CardContent>
        </Card>
      )}

      {/* Contents table */}
      {!loading && !error && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              {folders.length + files.length} items
            </CardTitle>
            <CardDescription>
              {folders.length} folders, {files.length} files
            </CardDescription>
          </CardHeader>
          <CardContent>
            {folders.length === 0 && files.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                This directory is empty.
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium w-8"></th>
                      <th className="text-left p-3 font-medium">Name</th>
                      <th className="text-left p-3 font-medium hidden sm:table-cell">Size</th>
                      <th className="text-left p-3 font-medium hidden md:table-cell">Last Modified</th>
                      <th className="text-left p-3 font-medium w-16">Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Folders */}
                    {folders.map((folder) => {
                      const displayName = folder.replace(prefix, '').replace(/\/$/, '')
                      return (
                        <tr
                          key={folder}
                          className="border-t hover:bg-muted/30 cursor-pointer"
                          onClick={() => navigateTo(folder)}
                        >
                          <td className="p-3 text-lg">
                            <span role="img" aria-label="folder">&#128193;</span>
                          </td>
                          <td className="p-3 font-medium text-primary hover:underline">
                            {displayName}/
                          </td>
                          <td className="p-3 text-sm text-muted-foreground hidden sm:table-cell">-</td>
                          <td className="p-3 text-sm text-muted-foreground hidden md:table-cell">-</td>
                          <td className="p-3">-</td>
                        </tr>
                      )
                    })}

                    {/* Files */}
                    {files.map((file) => {
                      const displayName = file.key.replace(prefix, '')
                      const fileUrl = `${S3_PUBLIC_URL}/${file.key}`
                      const isImage = isImageFile(file.key)
                      const isExpanded = expandedFile === file.key
                      return (
                        <React.Fragment key={file.key}>
                          <tr className="border-t hover:bg-muted/30">
                            <td className="p-3 text-lg">
                              {isImage
                                ? <span role="img" aria-label="image">&#128444;&#65039;</span>
                                : <span role="img" aria-label="file">&#128196;</span>
                              }
                            </td>
                            <td className="p-3 text-sm">
                              {isImage ? (
                                <button
                                  onClick={() => setExpandedFile(isExpanded ? null : file.key)}
                                  className="text-primary hover:underline font-medium text-left"
                                >
                                  {displayName}
                                </button>
                              ) : (
                                <span>{displayName}</span>
                              )}
                            </td>
                            <td className="p-3 text-sm text-muted-foreground hidden sm:table-cell">
                              {formatBytes(file.size)}
                            </td>
                            <td className="p-3 text-sm text-muted-foreground hidden md:table-cell">
                              {formatDate(file.lastModified)}
                            </td>
                            <td className="p-3">
                              {isImage && (
                                <button
                                  onClick={() => setExpandedFile(isExpanded ? null : file.key)}
                                  className="focus:outline-none focus:ring-2 focus:ring-primary rounded"
                                >
                                  <img
                                    src={fileUrl}
                                    alt={displayName}
                                    className={`w-10 h-10 rounded object-contain bg-muted transition-transform ${isExpanded ? 'ring-2 ring-primary' : 'hover:scale-110'}`}
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* Expanded preview row */}
                          {isImage && isExpanded && (
                            <tr className="bg-muted/20">
                              <td colSpan={5} className="p-4">
                                <div className="flex flex-col items-center gap-4">
                                  <img
                                    src={fileUrl}
                                    alt={displayName}
                                    className="max-w-full max-h-96 rounded-lg shadow-lg object-contain"
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                  <div className="flex gap-2">
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary hover:underline"
                                    >
                                      Open in new tab
                                    </a>
                                    <span className="text-muted-foreground">|</span>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(fileUrl)
                                      }}
                                      className="text-xs text-primary hover:underline"
                                    >
                                      Copy URL
                                    </button>
                                    <span className="text-muted-foreground">|</span>
                                    <button
                                      onClick={() => setExpandedFile(null)}
                                      className="text-xs text-muted-foreground hover:text-foreground"
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
